import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import AppBranding from './AppBranding';
import {
  ArrowLeft, Trophy, Medal, Skull, Zap, Turtle, Moon, Crown, RefreshCw,
  Users, Flame, PieChart, MoonStar, Sunrise, RotateCcw, Fish,
  Coins, Timer, Hand, Lock, Star,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_V2 = `${BACKEND_URL}/api/v2`;

// Map badge IDs to short display labels + colors
const SHOWCASE_BADGE_MAP = {
  first_trade: { label: 'First Blood', color: '#00FF41' },
  trades_10: { label: 'Getting Started', color: '#00FF41' },
  trades_50: { label: 'Active Trader', color: '#00FF41' },
  trades_100: { label: 'Centurion', color: '#FFD700' },
  trades_500: { label: 'Trading Machine', color: '#FFD700' },
  profit_1: { label: '$1 Profit', color: '#00FF41' },
  profit_10: { label: '$10 Profit', color: '#00FF41' },
  profit_100: { label: 'Benjamin', color: '#F7931A' },
  profit_1000: { label: 'Grand Slam', color: '#FFD700' },
  streak_3: { label: 'Hat Trick', color: '#FF4500' },
  streak_7: { label: 'On Fire', color: '#FF4500' },
  streak_30: { label: 'Unstoppable', color: '#FF4500' },
  diamond_hands: { label: 'Diamond Hands', color: '#06B6D4' },
  paper_hands: { label: 'Paper Hands', color: '#FF6B6B' },
  buy_high_sell_low: { label: 'Buy High Sell Low', color: '#FF6B6B' },
  dip_buyers_dip: { label: 'Dip Buyer', color: '#FF6B6B' },
  bag_holder: { label: 'Bag Holder', color: '#FF6B6B' },
  rug_pulled: { label: 'Rug Pulled', color: '#FF6B6B' },
  serial_loser: { label: 'Serial Loser', color: '#FF6B6B' },
  capitulation: { label: 'Capitulation', color: '#FF6B6B' },
  hodl_of_shame: { label: 'HODL of Shame', color: '#FF6B6B' },
  panic_seller: { label: 'Panic Seller', color: '#FF6B6B' },
  whale_wannabe: { label: 'Whale Wannabe', color: '#FF6B6B' },
  fee_factory: { label: 'Fee Factory', color: '#FF6B6B' },
  night_owl: { label: 'Night Owl', color: '#5B9BD5' },
  dawn_patrol: { label: 'Dawn Patrol', color: '#FFA500' },
  shib_lord: { label: 'SHIB Lord', color: '#FFA409' },
  doge_whisperer: { label: 'Doge Whisperer', color: '#C2A633' },
  diversified_af: { label: 'Diversified AF', color: '#8B5CF6' },
  one_trick_pony: { label: 'One Trick Pony', color: '#EC4899' },
};

// Reusable badge card component
const BadgeCard = ({ title, icon: Icon, borderColor, textColor, data, children, testId }) => (
  <Card className={`bg-[#0a0a0a] border-2`} style={{ borderColor }} data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2" style={{ color: textColor || borderColor }}>
        {Icon && <Icon className="w-5 h-5" />}
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      {data ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            {data.user_picture && <img src={data.user_picture} alt="" className="w-8 h-8 rounded-full" />}
            <span className="font-bold text-[#00FF41]">{data.user_name}</span>
          </div>
          {children}
          {data.badge?.description && (
            <Badge className="text-xs" style={{ backgroundColor: `${borderColor}22`, color: borderColor }}>
              {data.badge.description}
            </Badge>
          )}
        </div>
      ) : (
        <div className="text-[#008F11] text-sm">No data yet</div>
      )}
    </CardContent>
  </Card>
);

const HighScoresPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    if (!user) return;
    try {
      setRefreshing(true);
      const response = await axios.get(`${API_V2}/leaderboard/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const fmt = (v) => {
    if (v === undefined || v === null) return '$0.00';
    return `${v >= 0 ? '+' : ''}$${Math.abs(v).toFixed(2)}`;
  };

  const fmtDate = (d) => {
    if (!d || d === 'Never') return 'Never';
    try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-[#00FF41] font-mono animate-pulse">Loading High Scores...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-[#00FF41] font-mono">
      <AppBranding />
      <div className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-[#00FF41] hover:bg-[#003B00]">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Trophy className="w-8 h-8 text-yellow-500" /> HIGH SCORES
            </h1>
          </div>
          <Button variant="outline" size="sm" onClick={fetchStats} disabled={refreshing} className="border-[#00FF41] text-[#00FF41] hover:bg-[#003B00]">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Badge Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">

          {/* Total Traders */}
          <Card className="bg-[#0a0a0a] border-[#003B00]" data-testid="badge-total-traders">
            <CardHeader className="pb-2">
              <CardTitle className="text-[#00FF41] text-sm flex items-center gap-2">
                <Users className="w-5 h-5" /> TOTAL TRADERS
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-[#00FF41]">{stats?.total_traders || 0}</div>
              <p className="text-xs text-[#008F11] mt-1">Active on platform</p>
            </CardContent>
          </Card>

          {/* Best Trade */}
          <BadgeCard title={stats?.best_trade?.badge?.name || 'Trading Legend'} icon={Trophy} borderColor="#FFD700" data={stats?.best_trade} testId="badge-best-trade">
            <div className="text-2xl font-bold text-green-400">{fmt(stats?.best_trade?.profit)}</div>
            <div className="text-xs text-[#008F11]">{stats?.best_trade?.pair} &middot; {fmtDate(stats?.best_trade?.timestamp)}</div>
          </BadgeCard>

          {/* Worst Trade */}
          <BadgeCard title={stats?.worst_trade?.badge?.name || 'Diamond Hands... Oops'} icon={Skull} borderColor="#FF6B6B" data={stats?.worst_trade} testId="badge-worst-trade">
            <div className="text-2xl font-bold text-red-400">{fmt(stats?.worst_trade?.loss)}</div>
            <div className="text-xs text-[#008F11]">{stats?.worst_trade?.pair} &middot; {fmtDate(stats?.worst_trade?.timestamp)}</div>
          </BadgeCard>

          {/* Speed Demon */}
          <BadgeCard title={stats?.most_active_24h?.badge?.name || 'Speed Demon'} icon={Zap} borderColor="#00FF41" data={stats?.most_active_24h} testId="badge-speed-demon">
            <div className="text-2xl font-bold text-[#00FF41]">{stats?.most_active_24h?.trade_count} trades</div>
            <div className="text-xs text-[#008F11]">In the last 24 hours</div>
          </BadgeCard>

          {/* The Tortoise */}
          <BadgeCard title={stats?.least_active_24h?.badge?.name || 'The Tortoise'} icon={Turtle} borderColor="#4ECDC4" data={stats?.least_active_24h} testId="badge-tortoise">
            <div className="text-2xl font-bold text-cyan-400">{stats?.least_active_24h?.trade_count} trades</div>
            <div className="text-xs text-[#008F11]">In the last 24 hours</div>
          </BadgeCard>

          {/* Profit King */}
          <BadgeCard title={stats?.profit_king?.badge?.name || 'Profit King'} icon={Crown} borderColor="#F7931A" data={stats?.profit_king} testId="badge-profit-king">
            <div className="text-2xl font-bold text-orange-400">{fmt(stats?.profit_king?.total_profit)}</div>
            <div className="text-xs text-[#008F11]">Total lifetime profit</div>
          </BadgeCard>

          {/* Win Streak */}
          <BadgeCard title={stats?.win_streak?.badge?.name || 'On Fire'} icon={Flame} borderColor="#FF4500" data={stats?.win_streak} testId="badge-win-streak">
            <div className="text-2xl font-bold" style={{ color: '#FF4500' }}>{stats?.win_streak?.streak} wins</div>
            <div className="text-xs text-[#008F11]">Consecutive profitable trades</div>
          </BadgeCard>

          {/* Diversifier */}
          <BadgeCard title={stats?.diversifier?.badge?.name || 'The Diversifier'} icon={PieChart} borderColor="#8B5CF6" data={stats?.diversifier} testId="badge-diversifier">
            <div className="text-2xl font-bold text-purple-400">{stats?.diversifier?.coin_count} coins</div>
            <div className="text-xs text-[#008F11] truncate">{stats?.diversifier?.coins?.join(', ')}</div>
          </BadgeCard>

          {/* Night Owl */}
          <BadgeCard title={stats?.night_owl?.badge?.name || 'Night Owl'} icon={MoonStar} borderColor="#1E3A5F" textColor="#5B9BD5" data={stats?.night_owl} testId="badge-night-owl">
            <div className="text-2xl font-bold text-blue-400">{stats?.night_owl?.trade_count} trades</div>
            <div className="text-xs text-[#008F11]">Between midnight &amp; 6 AM UTC</div>
          </BadgeCard>

          {/* Early Bird */}
          <BadgeCard title={stats?.early_bird?.badge?.name || 'Early Bird'} icon={Sunrise} borderColor="#FFA500" data={stats?.early_bird} testId="badge-early-bird">
            <div className="text-2xl font-bold text-amber-400">{stats?.early_bird?.trade_count} trades</div>
            <div className="text-xs text-[#008F11]">Between 6 AM &amp; noon UTC</div>
          </BadgeCard>

          {/* Comeback Kid */}
          <BadgeCard title={stats?.comeback_kid?.badge?.name || 'Comeback Kid'} icon={RotateCcw} borderColor="#10B981" data={stats?.comeback_kid} testId="badge-comeback-kid">
            <div className="text-2xl font-bold text-emerald-400">+${stats?.comeback_kid?.recovery_amount}</div>
            <div className="text-xs text-[#008F11]">Recovery from deepest drawdown</div>
          </BadgeCard>

          {/* Whale */}
          <BadgeCard title={stats?.whale?.badge?.name || 'The Whale'} icon={Fish} borderColor="#3B82F6" data={stats?.whale} testId="badge-whale">
            <div className="text-2xl font-bold text-blue-400">${stats?.whale?.volume?.toLocaleString()}</div>
            <div className="text-xs text-[#008F11]">{stats?.whale?.pair} &middot; Single trade volume</div>
          </BadgeCard>

          {/* Penny Pincher */}
          <BadgeCard title={stats?.penny_pincher?.badge?.name || 'Penny Pincher'} icon={Coins} borderColor="#A3E635" data={stats?.penny_pincher} testId="badge-penny-pincher">
            <div className="text-2xl font-bold text-lime-400">{stats?.penny_pincher?.roi_pct}% ROI</div>
            <div className="text-xs text-[#008F11]">{fmt(stats?.penny_pincher?.profit)} profit on ${stats?.penny_pincher?.trade_size} trade</div>
          </BadgeCard>

          {/* Marathon Runner */}
          <BadgeCard title={stats?.marathon_runner?.badge?.name || 'Marathon Runner'} icon={Timer} borderColor="#EC4899" data={stats?.marathon_runner} testId="badge-marathon-runner">
            <div className="text-2xl font-bold text-pink-400">{stats?.marathon_runner?.duration_hours}h</div>
            <div className="text-xs text-[#008F11]">Trading session duration</div>
          </BadgeCard>

          {/* Paper Hands */}
          <BadgeCard title={stats?.paper_hands?.badge?.name || 'Paper Hands'} icon={Hand} borderColor="#F5F5DC" textColor="#D4D4AA" data={stats?.paper_hands} testId="badge-paper-hands">
            <div className="text-2xl font-bold" style={{ color: '#F5F5DC' }}>{stats?.paper_hands?.hold_display}</div>
            <div className="text-xs text-[#008F11]">{stats?.paper_hands?.pair} &middot; Fastest flip</div>
          </BadgeCard>

          {/* HODLer */}
          <BadgeCard title={stats?.hodler?.badge?.name || 'HODLer'} icon={Lock} borderColor="#06B6D4" data={stats?.hodler} testId="badge-hodler">
            <div className="text-2xl font-bold text-cyan-400">{stats?.hodler?.hold_display}</div>
            <div className="text-xs text-[#008F11]">{stats?.hodler?.pair} &middot; Diamond patience</div>
          </BadgeCard>

        </div>

        {/* Top Traders Leaderboard */}
        <Card className="bg-[#0a0a0a] border-[#003B00] mb-6" data-testid="top-traders-table">
          <CardHeader>
            <CardTitle className="text-[#00FF41] flex items-center gap-2">
              <Medal className="w-5 h-5 text-yellow-500" /> TOP 10 TRADERS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[300px]">
              <div className="space-y-2">
                {stats?.top_traders?.map((trader, index) => (
                  <div
                    key={trader.user_id}
                    className={`flex items-center justify-between p-3 rounded ${
                      index === 0 ? 'bg-yellow-500/10 border border-yellow-500/30' :
                      index === 1 ? 'bg-gray-400/10 border border-gray-400/30' :
                      index === 2 ? 'bg-orange-700/10 border border-orange-700/30' :
                      'bg-[#001a00] border border-[#003B00]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                        index === 0 ? 'bg-yellow-500 text-black' :
                        index === 1 ? 'bg-gray-400 text-black' :
                        index === 2 ? 'bg-orange-700 text-white' :
                        'bg-[#003B00] text-[#00FF41]'
                      }`}>{index + 1}</div>
                      {trader.user_picture && <img src={trader.user_picture} alt="" className="w-8 h-8 rounded-full" />}
                      <div>
                        <div className="font-bold text-[#00FF41] flex items-center gap-2 flex-wrap">
                          {trader.user_name}
                          {trader.showcase_badges?.length > 0 && (
                            <span className="flex items-center gap-1" data-testid={`showcase-${trader.user_id}`}>
                              {trader.showcase_badges.map(bid => {
                                const info = SHOWCASE_BADGE_MAP[bid];
                                if (!info) return null;
                                return (
                                  <span
                                    key={bid}
                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] font-bold rounded-sm border"
                                    style={{
                                      color: info.color,
                                      borderColor: `${info.color}66`,
                                      backgroundColor: `${info.color}15`,
                                    }}
                                    title={info.label}
                                  >
                                    <Star className="w-2.5 h-2.5 fill-current" />
                                    {info.label}
                                  </span>
                                );
                              })}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-[#008F11]">{trader.trade_count} trades</div>
                      </div>
                    </div>
                    <div className={`text-lg font-bold ${trader.total_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {fmt(trader.total_profit)}
                    </div>
                  </div>
                ))}
                {(!stats?.top_traders || stats.top_traders.length === 0) && (
                  <div className="text-center text-[#008F11] py-4">No traders yet</div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Wall of Shame */}
        <Card className="bg-[#0a0a0a] border-gray-600 mb-6" data-testid="wall-of-shame">
          <CardHeader>
            <CardTitle className="text-gray-400 flex items-center gap-2">
              <Moon className="w-5 h-5" /> WALL OF SHAME
              <span className="text-xs font-normal ml-2">(No trades in 24+ hours)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.wall_of_shame?.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {stats.wall_of_shame.map((shamer) => (
                  <div key={shamer.user_id} className="bg-gray-900/50 border border-gray-700 rounded-lg p-3 text-center">
                    {shamer.user_picture ? (
                      <img src={shamer.user_picture} alt="" className="w-12 h-12 rounded-full mx-auto mb-2 grayscale opacity-60" />
                    ) : (
                      <div className="w-12 h-12 rounded-full mx-auto mb-2 bg-gray-700 flex items-center justify-center">
                        <Moon className="w-6 h-6 text-gray-500" />
                      </div>
                    )}
                    <div className="text-gray-400 text-sm font-medium truncate">{shamer.user_name}</div>
                    <div className="text-xs text-gray-600 mt-1">Last: {fmtDate(shamer.last_trade)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-[#00FF41] py-4">Everyone's been trading! No shame today!</div>
            )}
          </CardContent>
        </Card>

        <div className="text-center text-xs text-[#008F11] mt-6">
          Last updated: {stats?.generated_at ? fmtDate(stats.generated_at) : 'Never'}
        </div>
      </div>
    </div>
  );
};

export default HighScoresPage;
